/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { classifyTier } from "@cosmos/airtable-data";
import { DirectoryCard } from "@cosmos/components";
import { useMessages } from "@epic-mw/i18n";

import { buildPartnerLinks, buildPartnerLogo, tierToTone } from "./buildPartnerCardProps";

import type { ServicePartner } from "@cosmos/airtable-data";

export interface ServicePartnerCardProps {
	data: ServicePartner;
}

/**
 * Adapter wrapping `DirectoryCard` for a `ServicePartners` row.
 *
 * Service partners have no tier/badge column in Airtable, so the badge is a
 * fixed, neutral "Service partner" chip (not derived from `classifyTier` like
 * the Training Partner card). The meta row stacks office locations + languages;
 * `Countries Supported` is a long list (25+) better suited to search/filter
 * than a location chip, so it's intentionally omitted from the card face.
 *
 * The "Learn More" button links to the partner's detail page
 * (`<directory path>/<partner slug>`) via `partner.learnMoreUrl`, which the
 * directory page builds from its own live path + the partner slug — so the
 * link is absent until the grid is rendered with a base path.
 */
export function ServicePartnerCard({ data: partner }: ServicePartnerCardProps): React.JSX.Element {
	const messages = useMessages();
	const badgeLabel = messages["epic.cosmos.directory.card.partner.service"] ?? "Service partner";

	const tier = classifyTier(partner.badgeLevel);
	// No tier data → neutral fallback icon (Users) when there's no logo.
	const logo = buildPartnerLogo(partner.logoUrl, tier);

	const { buttons, meta } = buildPartnerLinks(
		{ learnMoreUrl: partner.learnMoreUrl, languages: partner.languages },
		{
			learnMore: messages["epic.cosmos.plugins.learnMore"] ?? "Learn More",
		},
		{ officeLocation: partner.officeLocations.length > 0 ? partner.officeLocations.join(", ") : undefined },
	);

	return (
		<DirectoryCard
			name={partner.name}
			badge={{ label: badgeLabel, tone: tierToTone(tier) }}
			logo={logo}
			meta={meta}
			primaryTags={partner.industries}
			secondaryTags={partner.supportType}
			buttons={buttons}
		/>
	);
}
